/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { createPortal } from 'react-dom';
import { MdAnalytics } from 'react-icons/md';
import React, { useEffect, useState } from 'react';
import api from '../../../services/api';

import {
  Overlay,
  Modal,
  ModalHeader,
  HeaderTitle,
  CloseButton,
  Toolbar,
  ToolbarInfo,
  ToolbarSpacer,
  AutomationStatus,
  Body,
  Loading,
  CardsContainer,
  CardsColumn,
  Card,
  CardInfo,
  ContentGrid,
  Panel,
  PanelHeader,
  TableWrapper,
  Badge,
} from './styles';

function formatDate(value) {
  if (!value) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(new Date(value));
}

function formatNumber(value) {
  if (value === null || value === undefined) return '0';

  return Number(value).toLocaleString('pt-BR');
}

function formatPercent(value) {
  if (value === null || value === undefined) return '0%';

  return `${Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

function formatSeconds(value) {
  if (value === null || value === undefined) return '-';

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) return '-';

  if (numericValue < 60) return `${Math.round(numericValue)}s`;

  const min = Math.floor(numericValue / 60);
  const sec = Math.round(numericValue % 60);

  if (min < 60) return `${min}min ${sec}s`;

  const hour = Math.floor(min / 60);
  const remainingMin = min % 60;

  return `${hour}h ${remainingMin}min`;
}

function formatSecondsRange(minValue, maxValue) {
  if (
    minValue === null ||
    minValue === undefined ||
    maxValue === null ||
    maxValue === undefined
  ) {
    return '-';
  }

  const min = Number(minValue);
  const max = Number(maxValue);

  if (Number.isNaN(min) || Number.isNaN(max)) return '-';

  if (min === max) return formatSeconds(min);

  return `${formatSeconds(min)} a ${formatSeconds(max)}`;
}

function formatPageCatalog(page, catalog) {
  if (!page && !catalog) return '-';

  return `Pág. ${page || '-'} / Cat. ${catalog || '-'}`;
}

function getAutomationStatusVariant(health) {
  if (!health) return 'warning';

  return health.online ? 'success' : 'danger';
}

function getAutomationStatusText(health) {
  if (!health) return 'Verificando';

  return health.online ? 'Online' : 'Offline';
}

function formatMinutes(value) {
  if (value === null || value === undefined) return '-';

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) return '-';

  if (numericValue < 60) return `${Math.round(numericValue)} min`;

  const hour = Math.floor(numericValue / 60);
  const min = Math.round(numericValue % 60);

  if (hour < 24) return `${hour}h ${min}min`;

  const days = Math.floor(hour / 24);
  const remainingHours = hour % 24;

  return `${days}d ${remainingHours}h`;
}

function getFilaBadgeVariant(statusFila) {
  if (statusFila === 'CRITICO') return 'danger';
  if (statusFila === 'ATENCAO') return 'warning';

  return 'success';
}

export function BIDetectTextJobModal({ open, onClose }) {
  const [limit, setLimit] = useState(10);
  const [catalogo, setCatalogo] = useState('');
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  async function loadBI(currentLimit = limit, currentCatalogo = catalogo) {
    setLoading(true);

    try {
      const params = {
        limit: currentLimit || 10,
      };

      if (currentCatalogo) {
        params.catalogo = currentCatalogo;
      }

      const [biResponse, healthResponse] = await Promise.all([
        api.get('/bi/detect-text-job', {
          params,
        }),
        api.get('/bi/detect-text-job/health').catch(() => ({
          data: {
            online: false,
            latencyMs: null,
          },
        })),
      ]);

      setData(biResponse.data);
      setHealth(healthResponse.data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) {
      setCatalogo('');
      loadBI(10, '');
    }
  }, [open]);

  if (!open) return null;

  const resumo = data?.resumo;

  return createPortal(
    <Overlay>
      <Modal>
        <ModalHeader>
          <div>
            <HeaderTitle>
              <MdAnalytics size={30} />
              <h2>BI - Automação Mapeamento</h2>
            </HeaderTitle>
            <span>
              Monitoramento de automação do mapeamento, processamentos e fila
              DetectTextJob
            </span>
          </div>

          <CloseButton type="button" onClick={onClose}>
            ×
          </CloseButton>
        </ModalHeader>

        <Toolbar>
          <label>
            Registros por lista
            <input
              type="number"
              min={1}
              max={100}
              value={limit}
              onChange={(event) => setLimit(Number(event.target.value))}
            />
          </label>

          <label>
            Catálogo
            <select
              value={catalogo}
              onChange={(event) => setCatalogo(event.target.value)}
            >
              <option value="">Todos</option>

              {data?.catalogosFila?.map((item) => (
                <option
                  key={String(item.catalogo)}
                  value={String(item.catalogo)}
                >
                  Catálogo {item.catalogo}
                </option>
              ))}
            </select>
          </label>

          <button type="button" onClick={() => loadBI(limit, catalogo)}>
            Atualizar
          </button>

          <ToolbarInfo>
            <span>Total de catálogos na fila</span>
            <strong>
              {formatNumber(
                resumo?.totalCatalogosNaFilaGeral ??
                  resumo?.totalCatalogosNaFila
              )}
            </strong>
          </ToolbarInfo>

          <ToolbarSpacer />

          <AutomationStatus variant={getAutomationStatusVariant(health)}>
            <span>Status da automação</span>
            <strong>{getAutomationStatusText(health)}</strong>

            {health?.latencyMs !== null && health?.latencyMs !== undefined && (
              <small>{health.latencyMs} ms</small>
            )}
          </AutomationStatus>
        </Toolbar>

        <Body>
          {loading && <Loading>Carregando BI...</Loading>}

          {!loading && resumo && (
            <div>
              <CardsContainer>
                <CardsColumn>
                  <Card variant="success">
                    <span>Processados</span>
                    <strong>{formatNumber(resumo.totalProcessados)}</strong>
                    <small>{formatPercent(resumo.percentualProcessados)}</small>
                  </Card>
                  <Card variant="success">
                    <span>Processados hoje</span>
                    <strong>{formatNumber(resumo.processadosHoje)}</strong>
                  </Card>
                  <Card variant="success">
                    <span>Processados última hora</span>
                    <strong>
                      {formatNumber(resumo.processadosUltimaHora)}
                    </strong>
                  </Card>
                  <Card variant="success">
                    <span>Processados últimas 24h</span>
                    <strong>
                      {formatNumber(resumo.processadosUltimas24h)}
                    </strong>
                  </Card>
                </CardsColumn>

                <CardsColumn>
                  <Card>
                    <span>Total de páginas</span>
                    <strong>
                      {formatNumber(resumo.totalPaginas ?? resumo.totalJobs)}
                    </strong>
                  </Card>
                  <Card>
                    <span>Tempo desde último processamento</span>
                    <strong>
                      {formatMinutes(resumo.minutosDesdeUltimoProcessamento)}
                    </strong>
                  </Card>
                  <Card>
                    <span>Estimativa de término total</span>
                    <strong>
                      {formatSecondsRange(
                        resumo.estimativaTerminoTotalMinSegundos,
                        resumo.estimativaTerminoTotalMaxSegundos
                      )}
                    </strong>
                  </Card>
                </CardsColumn>

                <CardsColumn>
                  <Card variant="danger">
                    <span>Páginas com erro</span>
                    <strong>
                      {formatNumber(
                        resumo.paginasComErro ?? resumo.totalComErro
                      )}
                    </strong>
                    <small>{formatPercent(resumo.percentualErro)}</small>
                  </Card>
                  <Card variant="danger">
                    <span>Erros últimas 24h</span>
                    <strong>{formatNumber(resumo.errosUltimas24h)}</strong>
                  </Card>
                </CardsColumn>

                <CardsColumn>
                  <Card variant="warning">
                    <span>Na fila</span>
                    <strong>{formatNumber(resumo.totalNaFila)}</strong>
                    <small>{formatPercent(resumo.percentualFila)}</small>
                  </Card>
                  <Card
                    variant={resumo.possivelTravamento ? 'danger' : 'success'}
                  >
                    <span>Status da fila</span>
                    <strong>
                      {resumo.possivelTravamento ? 'Atenção' : 'Normal'}
                    </strong>
                    <small>
                      {resumo.possivelTravamento
                        ? 'Possível lentidão'
                        : 'Sem lentidão aparente'}
                    </small>
                  </Card>
                </CardsColumn>

                <CardsColumn>
                  <CardInfo>
                    <span>Página e Catálogo por último na fila</span>
                    <strong>
                      {formatPageCatalog(
                        resumo.ultimaPaginaFila,
                        resumo.ultimoCatalogoFila
                      )}
                    </strong>
                  </CardInfo>

                  <CardInfo>
                    <span>% de Imagens</span>
                    <strong>{formatPercent(resumo.percentualImagens)}</strong>
                    <small>
                      {formatNumber(resumo.totalImagensEditadas)} /{' '}
                      {formatNumber(resumo.totalGeralImagensAEditar)}
                    </small>
                  </CardInfo>

                  <CardInfo>
                    <span>Tempo para próxima página</span>
                    <strong>
                      {formatSecondsRange(
                        resumo.tempoProximaPaginaMinSegundos,
                        resumo.tempoProximaPaginaMaxSegundos
                      )}
                    </strong>
                  </CardInfo>

                  <CardInfo>
                    <span>Último processamento</span>
                    <strong>{formatDate(resumo.ultimoProcessamento)}</strong>
                  </CardInfo>

                  <CardInfo>
                    <span>Tempo médio até processar</span>
                    <strong>
                      {formatSeconds(resumo.mediaTempoAteProcessarSegundos)}
                    </strong>
                  </CardInfo>
                </CardsColumn>
              </CardsContainer>

              <ContentGrid>
                <Panel>
                  <PanelHeader>
                    <h3>Últimos processados</h3>
                    <span>{data?.processados?.length || 0} registros</span>
                  </PanelHeader>

                  <TableWrapper>
                    <table>
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Criado em</th>
                          <th>Finalizado em</th>
                          <th>Tempo até processar</th>
                          <th>Erro</th>
                          <th>Catálogo</th>
                          <th>Página</th>
                        </tr>
                      </thead>

                      <tbody>
                        {data?.processados?.map((item, index) => (
                          <tr key={`processado-${index}`}>
                            <td>
                              <Badge variant="success">OK</Badge>
                            </td>
                            <td>{formatDate(item.INSTINC)}</td>
                            <td>{formatDate(item.INSTLIDO)}</td>
                            <td>
                              {formatSeconds(
                                item.tempoAteProcessarSegundos ??
                                  item.duracaoSegundos
                              )}
                            </td>
                            <td>{item.DESC_ERROR || '-'}</td>
                            <td>{item.MBFILLER01 || '-'}</td>
                            <td>{item.MBFILLER02 || '-'}</td>
                          </tr>
                        ))}

                        {(!data?.processados ||
                          data.processados.length === 0) && (
                          <tr>
                            <td colSpan={7}>Nenhum registro processado.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </TableWrapper>
                </Panel>

                <Panel>
                  <PanelHeader>
                    <h3>Fila de processamento</h3>
                    <span>{data?.fila?.length || 0} registros</span>
                  </PanelHeader>

                  <TableWrapper>
                    <table>
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Criado em</th>
                          <th>Tempo na fila</th>
                          <th>Erro</th>
                          <th>Catálogo</th>
                          <th>Página</th>
                        </tr>
                      </thead>

                      <tbody>
                        {data?.fila?.map((item, index) => (
                          <tr key={`fila-${index}`}>
                            <td>
                              <Badge
                                variant={getFilaBadgeVariant(item.statusFila)}
                              >
                                {item.statusFila || 'NA FILA'}
                              </Badge>
                            </td>
                            <td>{formatDate(item.INSTINC)}</td>
                            <td>{formatMinutes(item.tempoFilaMinutos)}</td>
                            <td>{item.DESC_ERROR || '-'}</td>
                            <td>{item.MBFILLER01 || '-'}</td>
                            <td>{item.MBFILLER02 || '-'}</td>
                          </tr>
                        ))}

                        {(!data?.fila || data.fila.length === 0) && (
                          <tr>
                            <td colSpan={6}>Nenhum item na fila.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </TableWrapper>
                </Panel>
              </ContentGrid>
            </div>
          )}
        </Body>
      </Modal>
    </Overlay>,
    document.body
  );
}
